// Cliente de la API.
//
// Tipado contra @medchoice/shared: si la API cambia una respuesta, esto deja de
// compilar. La versión se declara una sola vez, en shared.

import {
  API_PREFIX, type Credenciales, type RespuestaError, type RespuestaSalud, type RespuestaSesion,
} from '@medchoice/shared';

/** Error con el mensaje que mandó la API, listo para mostrar en pantalla. */
export class ApiError extends Error {
  // Campo declarado aparte y no como propiedad de parámetro: esa sintaxis no es
  // borrable y erasableSyntaxOnly la rechaza.
  readonly status: number;

  constructor(status: number, mensaje: string) {
    super(mensaje);
    this.name = 'ApiError';
    this.status = status;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  /** Se manda como JSON. */
  body?: unknown;
  headers?: Record<string, string>;
  /**
   * Un 401 en cualquier pedido significa que la cookie venció y la sesión se da
   * por cerrada. La consulta de sesión es la excepción: ahí un 401 es la
   * respuesta normal de quien todavía no entró.
   */
  ignoreUnauthorized?: boolean;
}

let alVencerSesion: (() => void) | null = null;

export function onSessionExpired(accion: (() => void) | null): void {
  alVencerSesion = accion;
}

function texto(mensaje: string | string[]): string {
  return Array.isArray(mensaje) ? mensaje.join('. ') : mensaje;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ignoreUnauthorized = false, ...resto } = options;
  let respuesta: Response;
  try {
    respuesta = await fetch(API_PREFIX + path, {
      ...resto,
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', ...headers },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'No se pudo conectar con el servidor');
  }

  if (respuesta.status === 401 && !ignoreUnauthorized) alVencerSesion?.();

  const contenido: unknown = await respuesta.json().catch(() => null);
  if (!respuesta.ok) {
    const error = contenido as RespuestaError | null;
    throw new ApiError(
      respuesta.status,
      error ? texto(error.mensaje) : `Error ${respuesta.status}`,
    );
  }
  return contenido as T;
}

type QueryParams = Record<string, string | number | undefined>;

export function buildQuery(params: QueryParams): string {
  const partes: string[][] = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => [k, String(v)]);
  return partes.length > 0 ? `?${new URLSearchParams(partes).toString()}` : '';
}

export const api = {
  salud: () => request<RespuestaSalud>('/health'),

  sesionActual: () =>
    request<RespuestaSesion>('/sesion', { ignoreUnauthorized: true }).then((r) => r.usuario),

  ingresar: (credenciales: Credenciales) =>
    request<RespuestaSesion>('/sesion', {
      method: 'POST', body: credenciales, ignoreUnauthorized: true,
    }).then((r) => r.usuario),

  salir: () => request<null>('/sesion', { method: 'DELETE', ignoreUnauthorized: true }),
};
